import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaSave,
  FaTimes,
  FaCalculator,
  FaCheck,
  FaBan,
  FaSpinner,
  FaShieldAlt,
  FaChartLine,
  FaUniversity,
  FaCalendarAlt,
  FaInfoCircle,
} from "react-icons/fa";
import { HiOutlineSparkles } from "react-icons/hi";
import axios from "axios";
import toast from "react-hot-toast";

const CGPASchemeManager = () => {
  const [schemes, setSchemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingScheme, setEditingScheme] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [formData, setFormData] = useState({
    scheme: "",
    name: "",
    credits: Array(8).fill(0),
    description: "",
  });

  useEffect(() => {
    fetchSchemes();
    checkDarkMode();
  }, []);

  const checkDarkMode = () => {
    const dark = document.documentElement.getAttribute("data-theme") === "dark";
    setIsDarkMode(dark);

    const observer = new MutationObserver(() => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  };

  const fetchSchemes = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        "/api/cgpa/admin/schemes",
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setSchemes(response.data);
    } catch (error) {
      toast.error("Failed to fetch schemes");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleCreditChange = (index, value) => {
    const credits = [...formData.credits];
    credits[index] = parseInt(value) || 0;
    setFormData({ ...formData, credits });
  };

  const handleCreateScheme = async (e) => {
    e.preventDefault();
    if (!formData.scheme || !formData.name) {
      toast.error("Please fill all required fields");
      return;
    }

    if (formData.credits.some((c) => c <= 0)) {
      toast.error("All credits must be greater than 0");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        "/api/cgpa/admin/schemes",
        formData,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("Scheme created successfully");
      setShowAddForm(false);
      setFormData({
        scheme: "",
        name: "",
        credits: Array(8).fill(0),
        description: "",
      });
      fetchSchemes();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to create scheme");
    }
  };

  const handleUpdateScheme = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error("Please fill all required fields");
      return;
    }

    if (formData.credits.some((c) => c <= 0)) {
      toast.error("All credits must be greater than 0");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `/api/cgpa/admin/schemes/${editingScheme._id}`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("Scheme updated successfully");
      setEditingScheme(null);
      setFormData({
        scheme: "",
        name: "",
        credits: Array(8).fill(0),
        description: "",
      });
      fetchSchemes();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update scheme");
    }
  };

  const handleDeleteScheme = async (scheme) => {
    if (
      !window.confirm(
        `Are you sure you want to delete ${scheme.name}? This action cannot be undone.`,
      )
    ) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `/api/cgpa/admin/schemes/${scheme._id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("Scheme deleted successfully");
      fetchSchemes();
    } catch (error) {
      toast.error("Failed to delete scheme");
    }
  };

  const handleToggleStatus = async (scheme) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `/api/cgpa/admin/schemes/${scheme._id}`,
        {
          ...scheme,
          isActive: !scheme.isActive,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success(
        `Scheme ${!scheme.isActive ? "activated" : "deactivated"} successfully`,
      );
      fetchSchemes();
    } catch (error) {
      toast.error("Failed to update scheme status");
    }
  };

  const startEdit = (scheme) => {
    setEditingScheme(scheme);
    setFormData({
      scheme: scheme.scheme,
      name: scheme.name,
      credits: [...scheme.credits],
      description: scheme.description || "",
    });
  };

  const cancelEdit = () => {
    setEditingScheme(null);
    setFormData({
      scheme: "",
      name: "",
      credits: Array(8).fill(0),
      description: "",
    });
  };

  // Helper functions for styling
  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.8)" : "rgba(255, 255, 255, 0.9)";
  const getInputBackground = () => (isDarkMode ? "rgba(0,0,0,0.3)" : "#ffffff");

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 },
    },
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        >
          <FaSpinner size={48} color="#8b5cf6" />
        </motion.div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "calc(100vh - 200px)",
        background: isDarkMode ? "#0a0a0f" : "#f5f7fb",
        padding: "2rem",
        position: "relative",
      }}
    >
      {/* Animated Background Grid */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: "none",
          zIndex: 0,
          backgroundImage: `radial-gradient(circle at 1px 1px, ${isDarkMode ? "rgba(139,92,246,0.1)" : "rgba(139,92,246,0.08)"} 1px, transparent 1px)`,
          backgroundSize: "40px 40px",
        }}
      />

      {/* Floating Gradient Orbs */}
      <motion.div
        animate={{
          x: [0, 100, 0],
          y: [0, 50, 0],
        }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        style={{
          position: "fixed",
          top: "10%",
          left: "-5%",
          width: "300px",
          height: "300px",
          background:
            "radial-gradient(circle, rgba(139,92,246,0.3) 0%, transparent 70%)",
          borderRadius: "50%",
          filter: "blur(60px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <motion.div
        animate={{
          x: [0, -100, 0],
          y: [0, 80, 0],
        }}
        transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
        style={{
          position: "fixed",
          bottom: "10%",
          right: "-5%",
          width: "350px",
          height: "350px",
          background:
            "radial-gradient(circle, rgba(59,130,246,0.3) 0%, transparent 70%)",
          borderRadius: "50%",
          filter: "blur(60px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "2rem",
            flexWrap: "wrap",
            gap: "1rem",
          }}
        >
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                marginBottom: "0.5rem",
              }}
            >
              <div
                style={{
                  background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                  width: "40px",
                  height: "40px",
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FaCalculator size={20} color="white" />
              </div>
              <h2
                style={{
                  fontSize: "1.8rem",
                  fontWeight: "bold",
                  background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                CGPA Scheme Management
              </h2>
            </div>
            <p style={{ color: getTextSecondary(), marginLeft: "3rem" }}>
              Manage VTU credit schemes for CGPA calculation
            </p>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowAddForm(true)}
            style={{
              padding: "0.8rem 1.5rem",
              background: "linear-gradient(135deg, #10b981, #059669)",
              color: "white",
              border: "none",
              borderRadius: "12px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              fontWeight: "600",
              boxShadow: "0 4px 14px 0 rgba(16,185,129,0.4)",
            }}
          >
            <FaPlus /> Add New Scheme
          </motion.button>
        </motion.div>

        {/* Add/Edit Form Modal */}
        <AnimatePresence>
          {(showAddForm || editingScheme) && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(0,0,0,0.7)",
                backdropFilter: "blur(8px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
              }}
              onClick={() => {
                setShowAddForm(false);
                cancelEdit();
              }}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0, y: 30 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.9, opacity: 0, y: 30 }}
                transition={{ type: "spring", damping: 25 }}
                style={{
                  background: getCardBackground(),
                  backdropFilter: "blur(20px)",
                  borderRadius: "28px",
                  maxWidth: "700px",
                  width: "90%",
                  maxHeight: "90vh",
                  overflow: "auto",
                  padding: "2rem",
                  border: `1px solid ${getBorderColor()}`,
                  boxShadow: isDarkMode
                    ? "0 25px 50px -12px rgba(0,0,0,0.5)"
                    : "0 25px 50px -12px rgba(0,0,0,0.25)",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "1.5rem",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <div
                      style={{
                        background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                        width: "36px",
                        height: "36px",
                        borderRadius: "10px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {editingScheme ? (
                        <FaEdit size={16} color="white" />
                      ) : (
                        <FaPlus size={16} color="white" />
                      )}
                    </div>
                    <h3
                      style={{
                        color: getTextColor(),
                        fontSize: "1.3rem",
                        fontWeight: "bold",
                      }}
                    >
                      {editingScheme ? "Edit Scheme" : "Add New Scheme"}
                    </h3>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => {
                      setShowAddForm(false);
                      cancelEdit();
                    }}
                    style={{
                      background: isDarkMode
                        ? "rgba(139,92,246,0.1)"
                        : "rgba(139,92,246,0.05)",
                      border: "none",
                      width: "32px",
                      height: "32px",
                      borderRadius: "8px",
                      cursor: "pointer",
                      color: getTextSecondary(),
                    }}
                  >
                    <FaTimes />
                  </motion.button>
                </div>

                <form
                  onSubmit={
                    editingScheme ? handleUpdateScheme : handleCreateScheme
                  }
                >
                  <div style={{ marginBottom: "1.25rem" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "0.5rem",
                        color: getTextSecondary(),
                        fontWeight: "500",
                      }}
                    >
                      <FaCalendarAlt
                        style={{ marginRight: "0.4rem", color: "#8b5cf6" }}
                      />
                      Scheme Year <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="scheme"
                      value={formData.scheme}
                      onChange={handleInputChange}
                      disabled={!!editingScheme}
                      placeholder="e.g., 2023"
                      style={{
                        width: "100%",
                        padding: "0.8rem 1rem",
                        border: `2px solid ${getBorderColor()}`,
                        borderRadius: "12px",
                        outline: "none",
                        background: editingScheme
                          ? isDarkMode
                            ? "rgba(0,0,0,0.3)"
                            : "#f8f9fa"
                          : getInputBackground(),
                        color: getTextColor(),
                        fontSize: "0.9rem",
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: "1.25rem" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "0.5rem",
                        color: getTextSecondary(),
                        fontWeight: "500",
                      }}
                    >
                      <FaUniversity
                        style={{ marginRight: "0.4rem", color: "#8b5cf6" }}
                      />
                      Scheme Name <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="e.g., CBCS 2023 Scheme"
                      style={{
                        width: "100%",
                        padding: "0.8rem 1rem",
                        border: `2px solid ${getBorderColor()}`,
                        borderRadius: "12px",
                        outline: "none",
                        background: getInputBackground(),
                        color: getTextColor(),
                        fontSize: "0.9rem",
                      }}
                      onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                      onBlur={(e) =>
                        (e.target.style.borderColor = getBorderColor())
                      }
                    />
                  </div>

                  <div style={{ marginBottom: "1.25rem" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "0.5rem",
                        color: getTextSecondary(),
                        fontWeight: "500",
                      }}
                    >
                      <FaChartLine
                        style={{ marginRight: "0.4rem", color: "#8b5cf6" }}
                      />
                      Credits per Semester{" "}
                      <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(8, 1fr)",
                        gap: "0.5rem",
                      }}
                    >
                      {formData.credits.map((credit, idx) => (
                        <div key={idx}>
                          <label
                            style={{
                              fontSize: "0.65rem",
                              display: "block",
                              textAlign: "center",
                              color: getTextSecondary(),
                              marginBottom: "0.25rem",
                            }}
                          >
                            Sem {idx + 1}
                          </label>
                          <input
                            type="number"
                            value={credit || ""}
                            onChange={(e) =>
                              handleCreditChange(idx, e.target.value)
                            }
                            placeholder="0"
                            style={{
                              width: "100%",
                              padding: "0.5rem",
                              border: `2px solid ${getBorderColor()}`,
                              borderRadius: "10px",
                              textAlign: "center",
                              outline: "none",
                              background: getInputBackground(),
                              color: getTextColor(),
                            }}
                            onFocus={(e) =>
                              (e.target.style.borderColor = "#8b5cf6")
                            }
                            onBlur={(e) =>
                              (e.target.style.borderColor = getBorderColor())
                            }
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div style={{ marginBottom: "1.5rem" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "0.5rem",
                        color: getTextSecondary(),
                        fontWeight: "500",
                      }}
                    >
                      <FaInfoCircle
                        style={{ marginRight: "0.4rem", color: "#8b5cf6" }}
                      />
                      Description (Optional)
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows="3"
                      placeholder="Additional information about this scheme..."
                      style={{
                        width: "100%",
                        padding: "0.8rem 1rem",
                        border: `2px solid ${getBorderColor()}`,
                        borderRadius: "12px",
                        outline: "none",
                        resize: "vertical",
                        background: getInputBackground(),
                        color: getTextColor(),
                        fontSize: "0.9rem",
                      }}
                      onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                      onBlur={(e) =>
                        (e.target.style.borderColor = getBorderColor())
                      }
                    />
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: "1rem",
                      justifyContent: "flex-end",
                    }}
                  >
                    <motion.button
                      type="button"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        setShowAddForm(false);
                        cancelEdit();
                      }}
                      style={{
                        padding: "0.8rem 2rem",
                        background: isDarkMode
                          ? "rgba(100,116,139,0.3)"
                          : "#e2e8f0",
                        color: getTextColor(),
                        border: "none",
                        borderRadius: "12px",
                        cursor: "pointer",
                        fontWeight: "500",
                      }}
                    >
                      Cancel
                    </motion.button>
                    <motion.button
                      type="submit"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      style={{
                        padding: "0.8rem 2rem",
                        background: "linear-gradient(135deg, #10b981, #059669)",
                        color: "white",
                        border: "none",
                        borderRadius: "12px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        fontWeight: "600",
                      }}
                    >
                      <FaSave /> {editingScheme ? "Update" : "Create"}
                    </motion.button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Schemes List */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          style={{ display: "grid", gap: "1rem" }}
        >
          {schemes.length === 0 ? (
            <motion.div
              variants={itemVariants}
              style={{
                textAlign: "center",
                padding: "3rem",
                background: getCardBackground(),
                backdropFilter: "blur(20px)",
                borderRadius: "24px",
                border: `1px solid ${getBorderColor()}`,
              }}
            >
              <FaCalculator size={48} color={getTextSecondary()} />
              <p style={{ marginTop: "1rem", color: getTextSecondary() }}>
                No schemes found. Click "Add New Scheme" to create one.
              </p>
            </motion.div>
          ) : (
            schemes.map((scheme, idx) => (
              <motion.div
                key={scheme._id}
                variants={itemVariants}
                whileHover={{ y: -3 }}
                style={{
                  background: getCardBackground(),
                  backdropFilter: "blur(20px)",
                  borderRadius: "20px",
                  padding: "1.5rem",
                  boxShadow: isDarkMode
                    ? "0 10px 30px -8px rgba(0,0,0,0.3)"
                    : "0 10px 30px -8px rgba(0,0,0,0.1)",
                  borderLeft: `4px solid ${scheme.isActive ? "#10b981" : "#ef4444"}`,
                  border: `1px solid ${getBorderColor()}`,
                  borderLeftWidth: "4px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "start",
                    flexWrap: "wrap",
                    gap: "1rem",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "1rem",
                        flexWrap: "wrap",
                        marginBottom: "0.75rem",
                      }}
                    >
                      <h3
                        style={{
                          fontSize: "1.2rem",
                          fontWeight: "bold",
                          color: getTextColor(),
                        }}
                      >
                        {scheme.name}
                      </h3>
                      <span
                        style={{
                          padding: "0.25rem 0.75rem",
                          borderRadius: "20px",
                          fontSize: "0.7rem",
                          fontWeight: "500",
                          background: scheme.isActive
                            ? "linear-gradient(135deg, #10b981, #059669)"
                            : "linear-gradient(135deg, #ef4444, #dc2626)",
                          color: "white",
                        }}
                      >
                        {scheme.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>
                    <p
                      style={{
                        color: getTextSecondary(),
                        marginBottom: "0.5rem",
                        fontSize: "0.85rem",
                      }}
                    >
                      <strong>Scheme:</strong> {scheme.scheme}
                    </p>
                    <div style={{ marginBottom: "0.5rem" }}>
                      <strong
                        style={{
                          color: getTextSecondary(),
                          fontSize: "0.85rem",
                        }}
                      >
                        Credits:
                      </strong>
                      <div
                        style={{
                          display: "flex",
                          gap: "0.5rem",
                          flexWrap: "wrap",
                          marginTop: "0.5rem",
                        }}
                      >
                        {scheme.credits.map((credit, idx) => (
                          <span
                            key={idx}
                            style={{
                              background: isDarkMode
                                ? "rgba(139,92,246,0.1)"
                                : "rgba(139,92,246,0.05)",
                              padding: "0.25rem 0.75rem",
                              borderRadius: "20px",
                              fontSize: "0.7rem",
                              color: "#8b5cf6",
                              fontWeight: "500",
                            }}
                          >
                            Sem {idx + 1}: {credit}
                          </span>
                        ))}
                      </div>
                    </div>
                    {scheme.description && (
                      <p
                        style={{
                          color: getTextSecondary(),
                          fontSize: "0.8rem",
                          marginTop: "0.5rem",
                        }}
                      >
                        {scheme.description}
                      </p>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: "0.5rem" }}>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleToggleStatus(scheme)}
                      style={{
                        padding: "0.5rem",
                        background: scheme.isActive
                          ? "linear-gradient(135deg, #f59e0b, #d97706)"
                          : "linear-gradient(135deg, #10b981, #059669)",
                        color: "white",
                        border: "none",
                        borderRadius: "10px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.3rem",
                      }}
                      title={scheme.isActive ? "Deactivate" : "Activate"}
                    >
                      {scheme.isActive ? (
                        <FaBan size={14} />
                      ) : (
                        <FaCheck size={14} />
                      )}
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => startEdit(scheme)}
                      style={{
                        padding: "0.5rem",
                        background: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
                        color: "white",
                        border: "none",
                        borderRadius: "10px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.3rem",
                      }}
                      title="Edit"
                    >
                      <FaEdit size={14} />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleDeleteScheme(scheme)}
                      style={{
                        padding: "0.5rem",
                        background: "linear-gradient(135deg, #ef4444, #dc2626)",
                        color: "white",
                        border: "none",
                        borderRadius: "10px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.3rem",
                      }}
                      title="Delete"
                    >
                      <FaTrash size={14} />
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default CGPASchemeManager;

